// A simple tabs component following the WAI-ARIA Authoring Practices "Tabs"
// pattern (automatic activation): https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
// There's no other tablist in this codebase to crib from, so keyboard/ARIA
// wiring here is the reference implementation for future tab bars.
//
// This component only ever writes `dataset.open` on tabs/panels - how that
// maps to visible/hidden is entirely up to the consuming template's Tailwind
// classes. A plain `hidden data-[open=true]:block` works, but panels with
// very different heights will cause a layout jump when switching; consumers
// that care can instead grid-stack the panels (`grid` + `[grid-area:1/1]`
// on each) and toggle `opacity-0 data-[open=true]:opacity-100`, which keeps
// the container sized to the tallest panel and avoids the jump. Use opacity
// rather than `invisible`/`visible` for this: visibility is inherited but
// can be overridden by any descendant that sets its own explicit value (e.g.
// an expanded accordion item inside a panel), so a `visibility`-hidden panel
// can still "show through" - opacity can't be undone that way. `panel.inert`
// (set above) is what actually blocks interaction/focus/a11y on the inactive
// panel, since opacity alone doesn't.

const rootSelector = '[data-tabs-root]';

const selectors = {
    'list': selector('list'),
    'tab': selector('tab'),
    'panel': selector('panel'),
};

function selector(name) {
    return `[data-tabs-${name}]:not(& ${rootSelector} [data-tabs-${name}])`;
}

function initTabs(root) {
    // init() runs again on CMS content refresh - don't double-bind listeners
    if (root.dataset.tabsReady) return;
    root.dataset.tabsReady = 'true';

    const tabs = Array.from(root.querySelectorAll(selectors.tab));
    const panels = Array.from(root.querySelectorAll(selectors.panel));
    const list = root.querySelector(selectors.list);

    if (!tabs.length) return;

    const defaultTab = tabs.find(tab => tab.dataset.tabsDefault == 'true') || tabs[0];
    const state = { active: defaultTab.dataset.tabsTab };

    function update() {
        tabs.forEach(tab => {
            const isActive = tab.dataset.tabsTab == state.active;
            tab.dataset.open = isActive;
            tab.setAttribute('aria-selected', isActive);
            tab.tabIndex = isActive ? 0 : -1;
        });
        panels.forEach(panel => {
            const isActive = panel.dataset.tabsPanel == state.active;
            panel.dataset.open = isActive;
            panel.setAttribute('aria-hidden', !isActive);
            panel.inert = !isActive;
        });
    }

    function activate(tab) {
        if (state.active == tab.dataset.tabsTab) return;
        state.active = tab.dataset.tabsTab;
        update();
    }

    function maybeScrollTabIntoView(tab) {
        if (!list) return;
        const listRect = list.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        const isVisible = tabRect.left >= listRect.left && tabRect.right <= listRect.right;
        if (!isVisible) {
            tab.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
        }
    }

    tabs.forEach(tab => {
        tab.addEventListener('click', () => {
            activate(tab);
            maybeScrollTabIntoView(tab);
        });
    });

    if (list) {
        list.addEventListener('keydown', event => {
            const currentIndex = tabs.indexOf(event.target);
            if (currentIndex == -1) return;

            let nextIndex = null;
            switch (event.key) {
                case 'ArrowRight':
                case 'ArrowDown':
                    nextIndex = (currentIndex + 1) % tabs.length;
                    break;
                case 'ArrowLeft':
                case 'ArrowUp':
                    nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                    break;
                case 'Home':
                    nextIndex = 0;
                    break;
                case 'End':
                    nextIndex = tabs.length - 1;
                    break;
                default:
                    return;
            }

            event.preventDefault();
            const nextTab = tabs[nextIndex];
            activate(nextTab);
            nextTab.focus();
            maybeScrollTabIntoView(nextTab);
        });
    }

    update();
}

function init() {
    document.querySelectorAll(rootSelector).forEach(root => {
        initTabs(root);
    });
}

export default {
    init,
    initTabs
};
